import { Grid, Divider, TextField, Autocomplete, CircularProgress, FormControlLabel, Switch, MenuItem, Button, Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CardHeaderBar from '../CardHeaderBar/CardHeaderBar';
import EditButton from '../EditButton/EditButton';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { FC, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAccountById, updateAccount, fetchBuyerOrSupplierByName, fetchAccountSettings, updateAccountSettings } from "../../store/accountThunk";
import { fetchMeasuringUnits } from "../../store/inventoryStore/inventoryThunk";
import EmailListEditor from './EmailListEditor';

// Reports that support the Share-by-email feature. `key` matches the backend REPORT_KEY.
const SHARE_REPORT_OPTIONS: { key: string; label: string }[] = [
  { key: 'gstr1', label: 'GSTR-1 Sales Summary' },
  { key: 'tds', label: 'TDS Payable' },
  { key: 'dayBook', label: 'Day Book' },
  { key: 'profitLoss', label: 'Profit & Loss' },
  { key: 'balanceSheet', label: 'Balance Sheet' },
];

// WhatsApp notification toggles shown in the Settings tab (keys must match the backend model).
const WHATSAPP_NOTIFICATION_OPTIONS: { key: string; label: string }[] = [
  { key: 'invoiceCreated', label: 'Invoice created' },
  { key: 'paymentReceived', label: 'Payment received' },
  { key: 'paymentDueReminder', label: 'Payment due reminder' },
  { key: 'paymentOverdue', label: 'Payment overdue alert (the day after due date)' },
  { key: 'businessSummary', label: 'Daily business summary on WhatsApp' },
  { key: 'stockOutAlert', label: 'Stock out alert (to admin)' },
  { key: 'stockLowAlert', label: 'Stock low alert (to admin)' },
  { key: 'creditNoteCreated', label: 'Credit note created (to buyer)' },
  { key: 'debitNoteCreated', label: 'Debit note created (to supplier)' },
  { key: 'expenseRecorded', label: 'Expense recorded (to admin)' },
];

// People/HRMS WhatsApp notification toggles (keys must match the backend model).
// Same per-key toggles as the People mobile app's WhatsApp settings screen.
const PEOPLE_WHATSAPP_NOTIFICATION_OPTIONS: { key: string; label: string }[] = [
  { key: 'leaveStatusUpdate', label: 'Leave status update (to employee)' },
  { key: 'managerNewLeaveRequest', label: 'New leave request (to manager)' },
  { key: 'employeeWelcome', label: 'Employee welcome (when added)' },
  { key: 'payslipGenerated', label: 'Payslip generated (to employee)' },
  { key: 'salaryCredited', label: 'Salary credited (to employee)' },
  { key: 'shiftAssigned', label: 'Shift assigned (to employee)' },
  { key: 'documentRequired', label: 'Document required (to employee)' },
];

// Email notification toggles (keys must match the backend emailNotifications model).
const EMAIL_NOTIFICATION_OPTIONS: { key: string; label: string }[] = [
  { key: 'businessSummary', label: 'Daily business summary on email' },
  { key: 'shareReports', label: 'Share reports to recipients (when you Share a report)' },
];

// Selectable "days before due date" values for the payment due reminder.
const DUE_REMINDER_DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 10, 15];
const DEFAULT_DUE_REMINDER_DAYS = 2;

// Basic email shape check for the Business Summary extra-recipient input.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Phone: 8–15 digits, optional leading +. Kept permissive to allow country codes.
const PHONE_RE = /^\+?\d{8,15}$/;

// Selectable new-customer window values, in hours.
const NEW_CUSTOMER_WINDOW_OPTIONS = [12, 24, 36, 48, 72, 96, 120, 168];
const DEFAULT_NEW_CUSTOMER_WINDOW_HOURS = 24;

// Which card group to render. The Settings page tabs one section in at a time.
export type SettingsSection = 'notifications' | 'cashSale' | 'crm';

interface SettingsProps {
  section: SettingsSection;
}

const Settings: FC<SettingsProps> = ({ section }) => {
  const dispatch = useAppDispatch();

  const showNotifications = section === 'notifications';
  const showCashSale = section === 'cashSale';
  const showCrm = section === 'crm';

  // Cash sale account state
  const [options, setOptions] = useState<any[]>([]);
  const [selectedCashSale, setSelectedCashSale] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [savingCashSale, setSavingCashSale] = useState(false);

  // WhatsApp notification toggles
  const [waNotifications, setWaNotifications] = useState<any>(null);
  const [savingWaKey, setSavingWaKey] = useState<string | null>(null);

  // Email notification toggles
  const [emailNotifications, setEmailNotifications] = useState<any>(null);
  const [savingEmailKey, setSavingEmailKey] = useState<string | null>(null);

  // How many days before the due date the payment due reminder is sent.
  const [dueReminderDays, setDueReminderDays] = useState<number>(DEFAULT_DUE_REMINDER_DAYS);
  const [savingDueReminderDays, setSavingDueReminderDays] = useState(false);

  // Fallback low-stock thresholds, one row per unit, for items that don't set their own.
  const [stockLowRows, setStockLowRows] = useState<{ quantity: string; unit: any }[]>([]);
  const [savingStockLow, setSavingStockLow] = useState(false);
  const [unitOptions, setUnitOptions] = useState<any[]>([]);

  // Extra Business Summary recipients (in addition to the account's own email/WhatsApp number).
  const [summaryEmails, setSummaryEmails] = useState<string[]>([]);
  const [summaryPhones, setSummaryPhones] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [savingRecipients, setSavingRecipients] = useState(false);

  // Report share recipients: a master list applied to all reports, plus optional per-report lists.
  const [masterEmails, setMasterEmails] = useState<string[]>([]);
  const [perReportEmails, setPerReportEmails] = useState<Record<string, string[]>>({});
  const [selectedReportKey, setSelectedReportKey] = useState<string>(SHARE_REPORT_OPTIONS[0].key);
  const [savingShare, setSavingShare] = useState(false);
  // Which recipient mode the user is editing — one at a time to avoid confusion.
  const [shareMode, setShareMode] = useState<'master' | 'specific'>('master');

  // How long a buyer stays marked as a new customer after their first invoice.
  const [newCustomerWindowHours, setNewCustomerWindowHours] = useState<number>(DEFAULT_NEW_CUSTOMER_WINDOW_HOURS);
  const [savingWindowHours, setSavingWindowHours] = useState(false);

  const accountDetails = useAppSelector(state => state.account.accountDetails);

  // Debounce search input
  useEffect(() => {
    if (!showCashSale) return;
    setIsSearching(true);
    const timerId = setTimeout(() => {
      setDebouncedTerm(searchTerm);
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm, showCashSale]);

  // Fetch buyers when debounced term changes
  useEffect(() => {
    if (!accountDetails?._id || !showCashSale) return;
    setIsSearching(true);
    const fetchData = async () => {
      try {
        const queryString = `name=${debouncedTerm}&&searchFor=buyer`;
        const data: any = await dispatch(fetchBuyerOrSupplierByName(queryString)).unwrap();
        if (data) {
          const filtered = data.accounts.filter((item: any) => item._id !== accountDetails._id);
          setOptions(debouncedTerm ? filtered : filtered.slice(0, 5));
        }
      } catch (error) {
        console.error("Error fetching buyers:", error);
      }
      setIsSearching(false);
    };
    fetchData();
  }, [debouncedTerm]);

  // Handle buyer selection
  const handleCashSaleChange = async (_event: any, value: any) => {
    setSelectedCashSale(value);
    setSavingCashSale(true);
    try {
      const formData = new FormData();
      formData.append('defaultCashSaleAccount', value?._id || '');
      const responseData: any = await dispatch(updateAccount({
        id: accountDetails._id,
        accountPayload: formData as any,
      }));
      if (responseData && responseData?.status === 200) {
        dispatch(fetchAccountById(accountDetails._id));
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setSavingCashSale(false);
    }
  };

  // Load WhatsApp notification settings when the account is available.
  useEffect(() => {
    if (!accountDetails?._id || (!showNotifications && !showCrm)) return;
    const loadSettings = async () => {
      try {
        const res: any = await dispatch(fetchAccountSettings(accountDetails._id)).unwrap();
        if (res?.settings?.whatsappNotifications) {
          setWaNotifications(res.settings.whatsappNotifications);
        }
        if (res?.settings?.emailNotifications) {
          setEmailNotifications(res.settings.emailNotifications);
        }
        if (res?.settings?.paymentDueReminderDays) {
          setDueReminderDays(res.settings.paymentDueReminderDays);
        }
        if (res?.settings?.newCustomerWindowHours) {
          setNewCustomerWindowHours(res.settings.newCustomerWindowHours);
        }
        // Unit list first, so the saved threshold unit can resolve to an option.
        const unitData: any = await dispatch(fetchMeasuringUnits());
        if (!Array.isArray(unitData?.payload)) {
          console.error('Settings: measuring units did not load', unitData);
        }
        const units = (unitData?.payload || []).map((u: any) => ({ label: u.code ? `${u.name} (${u.code})` : u.name, value: u._id }));
        setUnitOptions(units);
        setStockLowRows((res?.settings?.stockLowThresholds || []).map((r: any) => ({
          quantity: String(r.quantity),
          unit: units.find((u: any) => String(u.value) === String(r.unit)) || null,
        })));
        if (res?.settings?.businessSummaryRecipients) {
          setSummaryEmails(res.settings.businessSummaryRecipients.emails || []);
          setSummaryPhones(res.settings.businessSummaryRecipients.phoneNumbers || []);
        }
        if (res?.settings?.reportShareRecipients) {
          const share = res.settings.reportShareRecipients;
          setMasterEmails(share.master?.emails || []);
          const per: Record<string, string[]> = {};
          SHARE_REPORT_OPTIONS.forEach(({ key }) => {
            per[key] = share.perReport?.[key]?.emails || [];
          });
          setPerReportEmails(per);
        }
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
    };
    loadSettings();
  }, [accountDetails?._id, showNotifications, showCrm]);

  // Toggle a single email notification type and persist it.
  const handleEmailToggle = async (key: string, checked: boolean) => {
    const previous = emailNotifications;
    setEmailNotifications({ ...(emailNotifications || {}), [key]: checked }); // optimistic
    setSavingEmailKey(key);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        emailNotifications: { [key]: checked },
      })).unwrap();
      if (res?.settings?.emailNotifications) {
        setEmailNotifications(res.settings.emailNotifications);
      }
    } catch (error) {
      console.error('Error updating email notification setting:', error);
      setEmailNotifications(previous); // revert on failure
    } finally {
      setSavingEmailKey(null);
    }
  };

  // Toggle a single WhatsApp notification type and persist it.
  const handleWaToggle = async (key: string, checked: boolean) => {
    const previous = waNotifications;
    setWaNotifications({ ...(waNotifications || {}), [key]: checked }); // optimistic
    setSavingWaKey(key);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        whatsappNotifications: { [key]: checked },
      })).unwrap();
      if (res?.settings?.whatsappNotifications) {
        setWaNotifications(res.settings.whatsappNotifications);
      }
    } catch (error) {
      console.error('Error updating WhatsApp notification setting:', error);
      setWaNotifications(previous); // revert on failure
    } finally {
      setSavingWaKey(null);
    }
  };

  // Change how many days before the due date the payment due reminder fires, and persist it.
  const handleDueReminderDaysChange = async (days: number) => {
    const previous = dueReminderDays;
    setDueReminderDays(days); // optimistic
    setSavingDueReminderDays(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        paymentDueReminderDays: days,
      })).unwrap();
      if (res?.settings?.paymentDueReminderDays) {
        setDueReminderDays(res.settings.paymentDueReminderDays);
      }
    } catch (error) {
      console.error('Error updating payment due reminder days:', error);
      setDueReminderDays(previous); // revert on failure
    } finally {
      setSavingDueReminderDays(false);
    }
  };

  // Change the new-customer window and persist it.
  const handleNewCustomerWindowChange = async (hours: number) => {
    const previous = newCustomerWindowHours;
    setNewCustomerWindowHours(hours); // optimistic
    setSavingWindowHours(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        newCustomerWindowHours: hours,
      })).unwrap();
      if (res?.settings?.newCustomerWindowHours) {
        setNewCustomerWindowHours(res.settings.newCustomerWindowHours);
      }
    } catch (error) {
      console.error('Error updating new customer window:', error);
      setNewCustomerWindowHours(previous); // revert on failure
    } finally {
      setSavingWindowHours(false);
    }
  };

  // Save the fallback low-stock rows. Incomplete rows are kept on screen but not sent.
  const handleStockLowSave = async (rows: { quantity: string; unit: any }[]) => {
    setSavingStockLow(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        stockLowThresholds: rows
          .filter((r) => r.unit?.value && r.quantity !== '' && Number(r.quantity) > 0)
          .map((r) => ({ quantity: Number(r.quantity), unit: r.unit.value })),
      })).unwrap();
      if (res?.settings?.stockLowThresholds === undefined) {
        console.error('Settings: stockLowThresholds missing from response — is the backend restarted?', res);
      }
    } catch (error) {
      console.error('Error updating low stock thresholds:', error);
    } finally {
      setSavingStockLow(false);
    }
  };

  const updateStockLowRow = (index: number, patch: any) => {
    const rows = stockLowRows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    setStockLowRows(rows);
    return rows;
  };

  // Persist the current recipient lists. Called after any add/remove; reverts on failure.
  const persistRecipients = async (emails: string[], phones: string[], previous: { emails: string[]; phones: string[] }) => {
    setSavingRecipients(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        businessSummaryRecipients: { emails, phoneNumbers: phones },
      })).unwrap();
      // Sync from the server-normalized lists (lowercased/de-duped/validated).
      if (res?.settings?.businessSummaryRecipients) {
        setSummaryEmails(res.settings.businessSummaryRecipients.emails || []);
        setSummaryPhones(res.settings.businessSummaryRecipients.phoneNumbers || []);
      }
    } catch (error) {
      console.error('Error updating business summary recipients:', error);
      setSummaryEmails(previous.emails); // revert on failure
      setSummaryPhones(previous.phones);
    } finally {
      setSavingRecipients(false);
    }
  };

  // Add a validated email to the recipient list and persist.
  const handleAddEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_RE.test(email)) { setEmailError('Enter a valid email address'); return; }
    if (summaryEmails.includes(email)) { setEmailError('This email is already added'); return; }
    const previous = { emails: summaryEmails, phones: summaryPhones };
    const next = [...summaryEmails, email];
    setSummaryEmails(next); // optimistic
    setEmailInput('');
    setEmailError(null);
    persistRecipients(next, summaryPhones, previous);
  };

  // Add a validated phone number to the recipient list and persist.
  const handleAddPhone = () => {
    const phone = phoneInput.trim();
    if (!phone) return;
    if (!PHONE_RE.test(phone)) { setPhoneError('Enter a valid phone number (8–15 digits)'); return; }
    if (summaryPhones.includes(phone)) { setPhoneError('This number is already added'); return; }
    const previous = { emails: summaryEmails, phones: summaryPhones };
    const next = [...summaryPhones, phone];
    setSummaryPhones(next); // optimistic
    setPhoneInput('');
    setPhoneError(null);
    persistRecipients(summaryEmails, next, previous);
  };

  // Remove an email from the recipient list and persist.
  const handleRemoveEmail = (email: string) => {
    const previous = { emails: summaryEmails, phones: summaryPhones };
    const next = summaryEmails.filter((e) => e !== email);
    setSummaryEmails(next); // optimistic
    persistRecipients(next, summaryPhones, previous);
  };

  // Remove a phone number from the recipient list and persist.
  const handleRemovePhone = (phone: string) => {
    const previous = { emails: summaryEmails, phones: summaryPhones };
    const next = summaryPhones.filter((p) => p !== phone);
    setSummaryPhones(next); // optimistic
    persistRecipients(summaryEmails, next, previous);
  };

  // Persist the master report-share recipient list; optimistic with revert on failure.
  const handleMasterEmailsChange = async (next: string[]) => {
    const previous = masterEmails;
    setMasterEmails(next); // optimistic
    setSavingShare(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        reportShareRecipients: { master: { emails: next } },
      })).unwrap();
      if (res?.settings?.reportShareRecipients?.master) {
        setMasterEmails(res.settings.reportShareRecipients.master.emails || []);
      }
    } catch (error) {
      console.error('Error updating master report recipients:', error);
      setMasterEmails(previous); // revert on failure
    } finally {
      setSavingShare(false);
    }
  };

  // Persist a per-report recipient list for the given report key; optimistic with revert on failure.
  const handlePerReportEmailsChange = async (reportKey: string, next: string[]) => {
    const previous = perReportEmails[reportKey] || [];
    setPerReportEmails((prev) => ({ ...prev, [reportKey]: next })); // optimistic
    setSavingShare(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        reportShareRecipients: { perReport: { [reportKey]: { emails: next } } },
      })).unwrap();
      const serverList = res?.settings?.reportShareRecipients?.perReport?.[reportKey]?.emails;
      if (serverList) {
        setPerReportEmails((prev) => ({ ...prev, [reportKey]: serverList }));
      }
    } catch (error) {
      console.error('Error updating per-report recipients:', error);
      setPerReportEmails((prev) => ({ ...prev, [reportKey]: previous })); // revert on failure
      setSavingShare(false);
    } finally {
      setSavingShare(false);
    }
  };

  // Sync selected value from account details
  useEffect(() => {
    let active = true;
    const settingsInfo = () => {
      try {
        if (active) {
          if (accountDetails.defaultCashSaleAccount && typeof accountDetails.defaultCashSaleAccount === 'object') {
            setSelectedCashSale(accountDetails.defaultCashSaleAccount);
          } else {
            setSelectedCashSale(null);
          }
        }
      } catch (error: any) {
        console.log('Setting catch block called');
      }
    };
    settingsInfo();
    return () => { active = false; };
  }, [accountDetails]);

  // Sits at the right end of the last threshold row so it lines up with the remove buttons.
  const addStockLowUnitButton = (
    <EditButton
      size='small'
      label='Add unit'
      icon={<AddOutlinedIcon />}
      onClick={() => setStockLowRows([...stockLowRows, { quantity: '', unit: null }])}
      disabled={savingStockLow || stockLowRows.length >= unitOptions.length}
    />
  );

  // One email toggle row; each lives on top of the recipients card it controls
  const emailToggle = (key: string) => {
    const option = EMAIL_NOTIFICATION_OPTIONS.find((o) => o.key === key);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!emailNotifications?.[key]}
              onChange={(e) => handleEmailToggle(key, e.target.checked)}
              disabled={emailNotifications === null || savingEmailKey === key}
              size="small"
            />
          }
          label={<span style={{ fontSize: 14, color: '#000' }}>{option?.label}</span>}
        />
        {savingEmailKey === key ? <CircularProgress size={16} /> : null}
      </div>
    );
  };

  // WhatsApp daily summary toggle, shown on the Business Summary Recipients card
  const waSummaryToggle = () => {
    const option = WHATSAPP_NOTIFICATION_OPTIONS.find((o) => o.key === 'businessSummary');
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!waNotifications?.businessSummary}
              onChange={(e) => handleWaToggle('businessSummary', e.target.checked)}
              disabled={waNotifications === null || savingWaKey === 'businessSummary'}
              size="small"
            />
          }
          label={<span style={{ fontSize: 14, color: '#000' }}>{option?.label}</span>}
        />
        {savingWaKey === 'businessSummary' ? <CircularProgress size={16} /> : null}
      </div>
    );
  };

  return (
    <Grid container spacing={2} alignItems="stretch">
      {/* Notifications tab, left column: WhatsApp Notifications */}
      {showNotifications && <Grid item xs={12} md={6}>
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10, height: '100%' }}>
          <CardHeaderBar title='WhatsApp Notifications' />
          <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
            Choose which WhatsApp messages this account sends. Each type can be turned on or off independently.
          </h5>
          <Grid container spacing={1}>
            {WHATSAPP_NOTIFICATION_OPTIONS.filter((option) => option.key !== 'businessSummary').map((option) => (
              <Grid item xs={12} key={option.key}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 480 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!waNotifications?.[option.key]}
                        onChange={(e) => handleWaToggle(option.key, e.target.checked)}
                        disabled={waNotifications === null || savingWaKey === option.key}
                        size="small"
                      />
                    }
                    label={<span style={{ fontSize: 14, color: '#000' }}>{option.label}</span>}
                  />
                  {savingWaKey === option.key ? <CircularProgress size={16} /> : null}
                </div>

                {/* Days-before selector for the payment due reminder. Shown only when that type is on. */}
                {option.key === 'paymentDueReminder' && !!waNotifications?.paymentDueReminder ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 46, marginTop: 2, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, color: '#9da7b7' }}>Send</span>
                    <TextField
                      select
                      size="small"
                      variant="standard"
                      value={dueReminderDays}
                      onChange={(e) => handleDueReminderDaysChange(Number(e.target.value))}
                      disabled={savingDueReminderDays}
                      sx={{ width: 64 }}
                      InputProps={{ disableUnderline: false }}
                    >
                      {DUE_REMINDER_DAY_OPTIONS.map((d) => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </TextField>
                    <span style={{ fontSize: 13, color: '#9da7b7' }}>day(s) before due date</span>
                    {savingDueReminderDays ? <CircularProgress size={14} /> : null}
                  </div>
                ) : null}

              </Grid>
            ))}
          </Grid>
        </div>
      </Grid>}

      {/* Default Cash Sale Account tab */}
      {showCashSale && <Grid item xs={12} md={6}>
            <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10 }}>
              <CardHeaderBar title='Default Cash Sale Account' />
              <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
                Select a buyer account to use as the default for cash sales
              </h5>
              <div style={{ display: 'flex', alignItems: 'center', maxWidth: 400 }}>
                <Autocomplete
                  sx={{ width: '100%' }}
                  value={selectedCashSale}
                  onChange={handleCashSaleChange}
                  onInputChange={(_e, value) => setSearchTerm(value)}
                  options={options}
                  getOptionLabel={(option: any) => option.name || ''}
                  isOptionEqualToValue={(option: any, value: any) => option._id === value?._id}
                  loading={isSearching}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Type here to search Buyer'
                      className='c-text-field'
                      variant="standard"
                      size='small'
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        endAdornment: (
                          <>
                            {isSearching || savingCashSale ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option: any) => (
                    <li {...props} key={option._id}>
                      {option.name || option.legalName || option._id}
                    </li>
                  )}
                />
              </div>
            </div>
      </Grid>}

      {/* CRM tab — only rendered when the Unofy CRM integration is on for this account. */}
      {showCrm && <Grid item xs={12} md={6}>
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 15 }}>
          <CardHeaderBar title='New Customer Window' />
          <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 10 }}>
            A buyer stays marked as new for every invoice raised within this window of their first
            invoice. The first invoice after the window marks them as an old customer.
          </h5>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#9da7b7' }}>Keep new for</span>
            <TextField
              select
              size="small"
              variant="standard"
              value={newCustomerWindowHours}
              onChange={(e) => handleNewCustomerWindowChange(Number(e.target.value))}
              disabled={savingWindowHours}
              sx={{ width: 80 }}
            >
              {NEW_CUSTOMER_WINDOW_OPTIONS.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </TextField>
            <span style={{ fontSize: 13, color: '#9da7b7' }}>hour(s) from the first invoice</span>
            {savingWindowHours ? <CircularProgress size={14} /> : null}
          </div>
        </div>
      </Grid>}

      {/* Notifications tab: People (HRMS) WhatsApp Notifications — same
          whatsappNotifications settings object, so it shares state + handler
          with the WhatsApp Notifications card above. */}
      {showNotifications && <Grid item xs={12} md={6}>
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10, height: '100%' }}>
          <CardHeaderBar title='People WhatsApp Notifications' />
          <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
            WhatsApp messages for the People module — leave, onboarding, payroll and workforce updates.
          </h5>
          <Grid container spacing={1}>
            {PEOPLE_WHATSAPP_NOTIFICATION_OPTIONS.map((option) => (
              <Grid item xs={12} key={option.key}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 480 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!waNotifications?.[option.key]}
                        onChange={(e) => handleWaToggle(option.key, e.target.checked)}
                        disabled={waNotifications === null || savingWaKey === option.key}
                        size="small"
                      />
                    }
                    label={<span style={{ fontSize: 14, color: '#000' }}>{option.label}</span>}
                  />
                  {savingWaKey === option.key ? <CircularProgress size={16} /> : null}
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      </Grid>}

      {/* Notifications tab: fallback low-stock thresholds, one row per unit. */}
      {showNotifications && <Grid item xs={12} md={6}>
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 15 }}>
          <CardHeaderBar title='Low Stock Alert Defaults' loading={savingStockLow} />
          <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 12 }}>
            Set a default per unit. Used only for items that don't have their own low stock quantity.
          </h5>

          {stockLowRows.map((row, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Autocomplete
                value={row.unit}
                options={unitOptions.filter((u: any) =>
                  !stockLowRows.some((r, i) => i !== index && String(r.unit?.value) === String(u.value)))}
                getOptionLabel={(o: any) => o.label || ''}
                isOptionEqualToValue={(o: any, v: any) => String(o.value) === String(v?.value)}
                onChange={(_e, value) => handleStockLowSave(updateStockLowRow(index, { unit: value }))}
                disabled={savingStockLow}
                sx={{ width: 170 }}
                renderInput={(params) => <TextField {...params} placeholder="Unit" variant="standard" size="small" />}
              />
              <span style={{ fontSize: 13, color: '#9da7b7', whiteSpace: 'nowrap' }}>Alert below</span>
              <TextField
                size="small"
                variant="standard"
                type="number"
                placeholder="Qty"
                value={row.quantity}
                onChange={(e) => updateStockLowRow(index, { quantity: e.target.value })}
                onBlur={() => handleStockLowSave(stockLowRows)}
                disabled={savingStockLow}
                sx={{ width: 80 }}
              />
              <Button
                size="small"
                onClick={() => {
                  const rows = stockLowRows.filter((_r, i) => i !== index);
                  setStockLowRows(rows);
                  handleStockLowSave(rows);
                }}
                sx={{ minWidth: 24, color: '#9da7b7' }}
              >✕</Button>
              {index === stockLowRows.length - 1 ? addStockLowUnitButton : null}
            </div>
          ))}
          {stockLowRows.length === 0 ? addStockLowUnitButton : null}
        </div>
      </Grid>}

      {/* Notifications tab: the two recipient cards share one column, stacked, each only as tall as its content.
          Business Summary Recipients is always shown because it holds the summary toggles; the recipient
          inputs appear once the matching summary toggle is ON. */}
      {showNotifications && <Grid item xs={12} md={6}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 15 }}>
          <CardHeaderBar title='Business Summary Recipients' loading={savingRecipients} />
          {emailToggle('businessSummary')}
          {waSummaryToggle()}
          <>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 12 }}>
              Extra people who also get the Daily Business Summary (besides this account's own email and WhatsApp).
              Emails go out only if the Email summary toggle is ON; WhatsApp numbers only if the WhatsApp summary toggle is ON.
            </h5>

            <Grid container spacing={3}>
              {/* Additional emails — only when the email Daily business summary toggle is ON */}
              {!!emailNotifications?.businessSummary && <Grid item xs={12}>
                <h5 style={{ fontWeight: 600, color: '#000', fontSize: 13, marginBottom: 8 }}>Additional Email Addresses</h5>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: 480 }}>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="Enter email address"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); if (emailError) setEmailError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEmail(); } }}
                    error={!!emailError}
                    helperText={emailError || ' '}
                    sx={{ flex: 1 }}
                  />
                  <EditButton
                    size='small'
                    label='Add'
                    icon={<AddOutlinedIcon />}
                    onClick={handleAddEmail}
                    disabled={savingRecipients || !emailInput.trim()}
                    sx={{ mt: 0.25 }}
                  />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {summaryEmails.length === 0
                    ? <span style={{ fontSize: 13, color: '#9da7b7' }}>No additional emails added.</span>
                    : summaryEmails.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => handleRemoveEmail(email)}
                        disabled={savingRecipients}
                        size="small"
                      />
                    ))}
                </div>
              </Grid>}

              {/* Additional phone numbers — only when the WhatsApp Daily business summary toggle is ON */}
              {!!waNotifications?.businessSummary && <Grid item xs={12}>
                <h5 style={{ fontWeight: 600, color: '#000', fontSize: 13, marginBottom: 8 }}>Additional WhatsApp Numbers</h5>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: 480 }}>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="e.g. +919876543210"
                    value={phoneInput}
                    onChange={(e) => { setPhoneInput(e.target.value); if (phoneError) setPhoneError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPhone(); } }}
                    error={!!phoneError}
                    helperText={phoneError || ' '}
                    sx={{ flex: 1 }}
                  />
                  <EditButton
                    size='small'
                    label='Add'
                    icon={<AddOutlinedIcon />}
                    onClick={handleAddPhone}
                    disabled={savingRecipients || !phoneInput.trim()}
                    sx={{ mt: 0.25 }}
                  />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {summaryPhones.length === 0
                    ? <span style={{ fontSize: 13, color: '#9da7b7' }}>No additional numbers added.</span>
                    : summaryPhones.map((phone) => (
                      <Chip
                        key={phone}
                        label={phone}
                        onDelete={() => handleRemovePhone(phone)}
                        disabled={savingRecipients}
                        size="small"
                      />
                    ))}
                </div>
              </Grid>}
            </Grid>
          </>
        </div>

        {/* Report Recipients: a master list applies to every shareable report; a per-report list adds extras for one report. */}
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 15 }}>
          <CardHeaderBar title='Report Recipients' loading={savingShare} />
          {emailToggle('shareReports')}
          {!!emailNotifications?.shareReports && <>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 12 }}>
              When you click Share on a report (GSTR-1, TDS, Day Book, Profit &amp; Loss, Balance Sheet), the report
              (PDF and Excel attached) is always emailed to this account's own email. The recipients below are added on top.
            </h5>

            {/* Mode selector — the user configures ONE of these at a time. */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={shareMode}
              onChange={(_e, val) => { if (val) setShareMode(val); }}
              sx={{
                mb: 2,
                '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 13, px: 2 },
                '& .Mui-selected': { backgroundColor: '#059669 !important', color: '#fff !important' },
              }}
            >
              <ToggleButton value="master">All reports</ToggleButton>
              <ToggleButton value="specific">Specific report</ToggleButton>
            </ToggleButtonGroup>

            {/* Master list: applies to all reports */}
            {shareMode === 'master' && (
              <Grid container>
                <Grid item xs={12}>
                  <EmailListEditor
                    label="Recipients for all reports"
                    emails={masterEmails}
                    onChange={handleMasterEmailsChange}
                    saving={savingShare}
                    emptyText="No shared recipients added."
                  />
                </Grid>
              </Grid>
            )}

            {/* Per-report list: pick a report, edit its extra recipients */}
            {shareMode === 'specific' && (
              <Grid container>
                <Grid item xs={12}>
                  <h5 style={{ fontWeight: 600, color: '#000', fontSize: 13, marginBottom: 8 }}>Choose a report</h5>
                  <TextField
                    select
                    size="small"
                    variant="standard"
                    value={selectedReportKey}
                    onChange={(e) => setSelectedReportKey(e.target.value)}
                    sx={{ minWidth: 220, mb: 1.5 }}
                  >
                    {SHARE_REPORT_OPTIONS.map((r) => (
                      <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>
                    ))}
                  </TextField>
                  <EmailListEditor
                    label={`Recipients for ${SHARE_REPORT_OPTIONS.find((r) => r.key === selectedReportKey)?.label || ''}`}
                    emails={perReportEmails[selectedReportKey] || []}
                    onChange={(next) => handlePerReportEmailsChange(selectedReportKey, next)}
                    saving={savingShare}
                    emptyText="No report-specific recipients added."
                  />
                </Grid>
              </Grid>
            )}
          </>}
        </div>
        </div>
      </Grid>}
    </Grid>
  );
};

export default Settings;
